import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AbstractControlOptions,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';

import { ToastrService } from 'ngx-toastr';

declare const require: any;

const zxcvbn = require('zxcvbn');

import { AdminsApiService } from '../../api';
import { LoginAlertService } from '../login-alert.service';

interface FormData {
  password: string;
  confirmPassword: string;
}

// TODO: Move to AdminsApiService
interface SetPasswordRequestPayload {
  password: string;
  confirmPassword: string;
  token: string;
}

// TODO: Move to AdminsApiService
interface CheckTokenResponse {
  email: string;
  invitationToken: string;
  resetToken: string;
}

@Component({ templateUrl: 'create-password.component.html' })
export class CreatePasswordComponent implements OnInit {
  registerForm: FormGroup;

  isLoading = false;
  admin: CheckTokenResponse = {
    email: null,
    invitationToken: null,
    resetToken: null,
  };

  passwordStrength = 0;
  passwordFeedback: string = null;

  private token = null;
  private formSubmitAttempt = false;

  constructor(
    private adminService: AdminsApiService,
    private alertService: LoginAlertService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService
  ) {
    const passwordStrengthValidator = (form: FormGroup) => {
      const password = form.get('password').value || '';

      const { score, feedback } = zxcvbn(password);
      this.passwordStrength = score;
      this.passwordFeedback = feedback.warning;

      if (score < 4) {
        return { tooLow: true };
      }
    };

    const passwordConfirmValidator = (form: FormGroup) => {
      const password = form.get('password').value;
      const confirmPassword = form.get('confirmPassword').value;

      if (password !== confirmPassword) {
        return { passwordConfirm: true };
      }
    };

    this.registerForm = this.fb.group(
      {
        password: ['', Validators.required],
        confirmPassword: ['', Validators.required],
      },
      {
        validators: [
          passwordConfirmValidator,
          passwordStrengthValidator.bind(this),
        ],
      } as AbstractControlOptions
    );
  }

  public ngOnInit() {
    this.admin = this.route.snapshot.data.admin;
    this.token = this.route.snapshot.params.token;
  }

  isInvalidPassword(field: string = 'password'): boolean {
    return !!(
      this.registerForm.errors &&
      this.registerForm.errors.tooLow &&
      (this.registerForm.get(field).touched || this.formSubmitAttempt)
    );
  }

  isInvalidConfirmPassword(field: string = 'confirmPassword'): boolean {
    return !!(
      this.registerForm.errors &&
      this.registerForm.errors.passwordConfirm &&
      (this.registerForm.get(field).touched || this.formSubmitAttempt)
    );
  }

  onSubmit() {
    this.formSubmitAttempt = true;

    if (this.registerForm.invalid) {
      return;
    }

    const formData: FormData = this.registerForm.value;

    const handleNext = () => {
      this.alertService.setAlert({
        type: 'success',
        message: `Password for ${this.admin.email} has been updated.`,
      });
      this.router.navigateByUrl('/login');
    };

    const handleError = (err) => {
      this.formSubmitAttempt = false;
      this.isLoading = false;

      switch (err.status) {
        case 422:
          this.toastr.error(
            'Password is too weak. Please use a stronger password.',
            'Invalid password!'
          );
          break;

        default:
          this.toastr.error(
            'Please try again or contact support.',
            'Request failed!'
          );
          break;
      }
    };

    if (
      this.admin.invitationToken &&
      this.token === this.admin.invitationToken
    ) {
      this.isLoading = true;

      const admin: SetPasswordRequestPayload = {
        ...formData,
        token: this.token,
      };

      this.adminService.createPassword(admin).subscribe({
        next: handleNext,
        error: handleError,
      });
    } else if (this.admin.resetToken && this.token === this.admin.resetToken) {
      this.isLoading = true;

      const admin: SetPasswordRequestPayload = {
        ...formData,
        token: this.token,
      };

      this.adminService.resetPassword(admin).subscribe({
        next: handleNext,
        error: handleError,
      });
    }
  }
}
